/**
 * Board colours and piece styling, as CSS custom properties on the document
 * root.
 *
 * Nothing renders differently because a theme exists: `Board` already draws its
 * squares from `--color-board-light` / `--color-board-dark`, so a theme is a
 * table of variable values and not a single conditional in a component.
 */
export interface BoardTheme {
  readonly id: string
  readonly label: string
  readonly light: string
  readonly dark: string
  /** Coordinate labels take the colour of the opposite square, as chess.com does. */
  readonly coordOnLight: string
  readonly coordOnDark: string
  /** The last-move and selection wash, over both square colours. */
  readonly highlight: string
}

export const BOARD_THEMES: readonly BoardTheme[] = [
  {
    id: 'green',
    label: 'Green',
    light: '#ebecd0',
    dark: '#739552',
    coordOnLight: '#739552',
    coordOnDark: '#ebecd0',
    highlight: 'rgb(255 255 51 / 0.5)',
  },
  {
    id: 'walnut',
    label: 'Walnut',
    light: '#f0d9b5',
    dark: '#b58863',
    coordOnLight: '#b58863',
    coordOnDark: '#f0d9b5',
    highlight: 'rgb(255 214 0 / 0.45)',
  },
  {
    id: 'ice',
    label: 'Ice',
    light: '#e8eef4',
    dark: '#7a9db8',
    coordOnLight: '#7a9db8',
    coordOnDark: '#e8eef4',
    highlight: 'rgb(120 200 255 / 0.55)',
  },
  {
    id: 'slate',
    label: 'Slate',
    light: '#dfe0e2',
    dark: '#8b8d92',
    coordOnLight: '#8b8d92',
    coordOnDark: '#dfe0e2',
    highlight: 'rgb(255 255 255 / 0.45)',
  },
  {
    id: 'midnight',
    label: 'Midnight',
    light: '#5a5f7a',
    dark: '#2f3350',
    coordOnLight: '#2f3350',
    coordOnDark: '#9aa0c0',
    highlight: 'rgb(140 160 255 / 0.5)',
  },
]

/**
 * Piece sets.
 *
 * Honestly: these are restyles of one set of paths, not three hand-drawn sets.
 * Fill, outline weight and shadow, driven by variables the SVG reads. Three
 * genuinely different Staunton sets is days of path work, and a restyle that
 * looks deliberate beats an original that looks wrong.
 */
export interface PieceSet {
  readonly id: string
  readonly label: string
  readonly note: string
  readonly whiteFill: string
  readonly whiteStroke: string
  readonly whiteDetail: string
  readonly blackFill: string
  readonly blackStroke: string
  readonly blackDetail: string
  readonly strokeWidth: number
  readonly shadow: string
}

export const PIECE_SETS: readonly PieceSet[] = [
  {
    id: 'classic',
    label: 'Classic',
    note: 'Warm ivory and charcoal',
    whiteFill: '#fcfcfa',
    whiteStroke: '#4a4744',
    whiteDetail: '#4a4744',
    blackFill: '#3b3936',
    blackStroke: '#16150f',
    blackDetail: '#c9c6c2',
    strokeWidth: 1.5,
    shadow: 'drop-shadow(0 1px 1px rgb(0 0 0 / 0.25))',
  },
  {
    id: 'bold',
    label: 'Bold',
    note: 'Heavy outlines, high contrast',
    whiteFill: '#ffffff',
    whiteStroke: '#111111',
    whiteDetail: '#111111',
    blackFill: '#1b1b1b',
    blackStroke: '#000000',
    blackDetail: '#ffffff',
    strokeWidth: 2.4,
    shadow: 'drop-shadow(0 2px 2px rgb(0 0 0 / 0.35))',
  },
  {
    id: 'minimal',
    label: 'Minimal',
    note: 'Thin lines, no shadow',
    whiteFill: '#fbfbf8',
    whiteStroke: '#8a8683',
    whiteDetail: '#8a8683',
    blackFill: '#4d4a47',
    blackStroke: '#2a2825',
    blackDetail: '#d8d5d1',
    strokeWidth: 0.9,
    shadow: 'none',
  },
]

export interface Prefs {
  readonly board: string
  readonly pieces: string
}

export const DEFAULT_PREFS: Prefs = { board: 'green', pieces: 'classic' }

export const findBoardTheme = (id: string): BoardTheme =>
  BOARD_THEMES.find((theme) => theme.id === id) ?? (BOARD_THEMES[0] as BoardTheme)

export const findPieceSet = (id: string): PieceSet =>
  PIECE_SETS.find((set) => set.id === id) ?? (PIECE_SETS[0] as PieceSet)

/**
 * Write a theme into a DOM element's inline custom properties.
 *
 * Takes an element rather than assuming `document.documentElement` so the
 * settings sheet can preview a theme on one small board without the rest of the
 * page flickering through every option as you scrub the list.
 */
export const applyPrefs = (element: HTMLElement, prefs: Prefs): void => {
  const board = findBoardTheme(prefs.board)
  const pieces = findPieceSet(prefs.pieces)

  const set = (name: string, value: string): void => element.style.setProperty(name, value)

  set('--color-board-light', board.light)
  set('--color-board-dark', board.dark)
  set('--color-coord-on-light', board.coordOnLight)
  set('--color-coord-on-dark', board.coordOnDark)
  set('--board-highlight', board.highlight)

  set('--piece-white-fill', pieces.whiteFill)
  set('--piece-white-stroke', pieces.whiteStroke)
  set('--piece-white-detail', pieces.whiteDetail)
  set('--piece-black-fill', pieces.blackFill)
  set('--piece-black-stroke', pieces.blackStroke)
  set('--piece-black-detail', pieces.blackDetail)
  set('--piece-stroke-width', String(pieces.strokeWidth))
  set('--piece-shadow', pieces.shadow)
}
